/**
 * Persistent photo blob cache.
 *
 * Fetches a remote image URL once, converts it to a base64 data URL, and
 * stores it in localStorage with a 7-day TTL.  Subsequent renders of the
 * Avatar component read the data URL directly from localStorage — no further
 * network requests are made to lh3.googleusercontent.com (or any other image
 * host), eliminating 429 Too Many Requests errors.
 *
 * Concurrent callers for the same URL share a single in-flight Promise so only
 * one HTTP request is ever made regardless of how many Avatar instances or
 * App.tsx call-sites fire at the same moment.
 */

const CACHE_PREFIX = 'smb_photo_v1_';
const CACHE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CachedEntry {
  dataUrl: string;
  expiresAt: number;
}

/** In-flight fetch promises keyed by URL — deduplicates concurrent callers. */
const inFlight = new Map<string, Promise<string | null>>();

/** Derive a stable, safe localStorage key from a photo URL. */
function storageKey(url: string): string {
  try {
    return CACHE_PREFIX + btoa(url).slice(0, 60);
  } catch {
    let h = 0;
    for (let i = 0; i < url.length; i++) {
      h = (Math.imul(31, h) + url.charCodeAt(i)) >>> 0;
    }
    return CACHE_PREFIX + h.toString(36);
  }
}

/**
 * Synchronously read a cached data URL from localStorage.
 * Returns null if the entry is missing, malformed, or expired.
 */
export function getCachedPhotoDataUrl(url: string): string | null {
  if (!url) return null;
  try {
    const raw = localStorage.getItem(storageKey(url));
    if (!raw) return null;
    const entry: CachedEntry = JSON.parse(raw);
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(storageKey(url));
      return null;
    }
    return entry.dataUrl;
  } catch {
    return null;
  }
}

/**
 * Fetch a remote image and store it as a base64 data URL in localStorage.
 *
 * Key guarantee: only ONE network request is made per URL at any given time.
 * All concurrent callers receive the same Promise and share the result.
 * Returns the data URL on success, or null on any failure (network, CORS,
 * storage quota, etc.) — callers should fall back to the original remote URL.
 */
export function fetchAndCachePhotoDataUrl(url: string): Promise<string | null> {
  if (!url) return Promise.resolve(null);

  // 1. Serve from localStorage if still fresh — no network needed.
  const cached = getCachedPhotoDataUrl(url);
  if (cached) return Promise.resolve(cached);

  // 2. If a fetch is already in-flight for this URL, reuse it.
  const existing = inFlight.get(url);
  if (existing) return existing;

  // 3. Start a new fetch and register it so concurrent callers join it.
  const promise = (async (): Promise<string | null> => {
    try {
      const response = await fetch(url, { mode: 'cors', cache: 'force-cache' });
      if (!response.ok) {
        console.warn(`[Photo Cache] Fetch failed (${response.status}) for: ${url.slice(0, 60)}…`);
        return null;
      }

      const blob = await response.blob();

      return await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          try {
            const entry: CachedEntry = {
              dataUrl,
              expiresAt: Date.now() + CACHE_DURATION_MS,
            };
            localStorage.setItem(storageKey(url), JSON.stringify(entry));
            console.log('[Photo Cache] Photo persisted to localStorage (7-day TTL)');
          } catch {
            console.warn('[Photo Cache] localStorage write failed (quota exceeded?)');
          }
          resolve(dataUrl);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('[Photo Cache] Fetch error:', err);
      return null;
    } finally {
      // Always remove from in-flight map so a future retry can start fresh.
      inFlight.delete(url);
    }
  })();

  inFlight.set(url, promise);
  return promise;
}

/** Remove a cached entry (e.g. after sign-out or photo change). */
export function evictCachedPhoto(url: string): void {
  if (!url) return;
  try {
    localStorage.removeItem(storageKey(url));
  } catch {
    // ignore
  }
}

/** Purge all entries written by this cache (identified by the key prefix). */
export function evictAllCachedPhotos(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
